import React from 'react'
import {
    Flex,
    Heading,
    LinkBox,
    LinkOverlay,
    Skeleton,
    Text,
    chakra,
} from '@chakra-ui/react'
import Link from 'next/link'
import { ArrowRight } from '@emotion-icons/heroicons-outline/ArrowRight'
import { Company, getCompanyMainLocationCountry } from 'entities/company'
import { skeletonLocation } from 'entities/location.mock'
import { routes } from 'routes'
import { CardWrapper } from 'components/CardWrapper'
import { Avatar } from 'components/images/Avatar'

const ArrowRightIcon = chakra(ArrowRight)

const descNoOfLines = 7
const descLineHeight = '24px'

export const CompanyMiniCard = ({
    company,
}: {
    company?: Pick<
        Company,
        | 'name'
        | 'slug'
        | 'logo'
        | 'description'
        | 'locations'
        | 'profileEnabled'
        | 'totalPublishedJobCount'
    >
}) => {
    const { name, slug, logo, description, profileEnabled, totalPublishedJobCount = 0 } =
        company ?? {}

    const isLoaded = Boolean(name)
    const descMaxH = `calc(${descNoOfLines} * ${descLineHeight})`

    const companyProfileURL = profileEnabled && slug ? routes.companyProfile(slug) : '#'
    const companyJobsURL = slug ? routes.companyJobs({ companySlug: slug }) : '#'

    return (
        <LinkBox
            as={CardWrapper}
            alignContent="space-between"
            gridGap="12px"
            borderWidth="0px"
        >
            <Flex gridGap="10px" mb="12px">
                {logo && (
                    <Avatar
                        variant="company"
                        src={logo}
                        boxSize="52px"
                        isLoaded={isLoaded}
                    />
                )}
                <Flex direction="column" alignSelf="center" gridGap="1px">
                    <Skeleton isLoaded={isLoaded}>
                        <Link href={companyProfileURL} passHref>
                            <LinkOverlay>
                                <Heading as="h3" size="sm" noOfLines={1}>
                                    {name}
                                </Heading>
                            </LinkOverlay>
                        </Link>
                    </Skeleton>
                    <Skeleton isLoaded={isLoaded}>
                        <Text
                            variant="bodyTextSmall"
                            // NB: For "New York, United States of America",
                            //     on `global-min=320px`, it otherwise stretches the UI.
                            overflowWrap="anywhere"
                            noOfLines={1}
                        >
                            {isLoaded
                                ? getCompanyMainLocationCountry(company)
                                : skeletonLocation.country}
                        </Text>
                    </Skeleton>
                </Flex>
            </Flex>

            {/* TODO [DEV-788]: Hidden until the 'flair' is implemented. */}
            {/*<Skeleton isLoaded={isLoaded} w="fit-content">*/}
            {/*    <Box*/}
            {/*        p="4px 10px"*/}
            {/*        borderRadius="6px"*/}
            {/*        w="fit-content"*/}
            {/*        backgroundColor="secondary.xlight"*/}
            {/*    >*/}
            {/*        <Text color="secondary.text" variant="functionalSemiBold">*/}
            {/*            Responds quickly!*/}
            {/*        </Text>*/}
            {/*    </Box>*/}
            {/*</Skeleton>*/}

            <Skeleton
                isLoaded={isLoaded}
                maxH={descMaxH}
                mb="4px"
                display={isLoaded && !description ? 'none' : undefined}
            >
                <Text variant="bodyTextSmall" noOfLines={descNoOfLines}>
                    {description}
                </Text>
            </Skeleton>

            <Skeleton isLoaded={isLoaded} w="fit-content">
                <Link href={companyJobsURL} passHref>
                    <Flex as="a" alignItems="center" gridGap="8px">
                        <Text
                            variant="functionalButtonsLarge"
                            fontWeight={600}
                            letterSpacing="-0.01em"
                            color="secondary.text"
                        >
                            {'All live roles'}
                            {totalPublishedJobCount > 0
                                ? ` (${totalPublishedJobCount})`
                                : ''}
                        </Text>
                        <ArrowRightIcon
                            color="secondary.text"
                            size="19px"
                            strokeWidth="1.5px"
                        />
                    </Flex>
                </Link>
            </Skeleton>
        </LinkBox>
    )
}
